// Ordering for human-numbered names — yard locations, racking rows, warehouse bins — so they read
// the way an operator sees them on the rack.
//
// A plain string sort compares one character at a time and never sees the number, so "Row 10" lands
// above "Row 2" and an operator hunting for "Row 9" finds it at the bottom of the list.
//
// The API sorts these lists too (GPMetals.Core/Helpers/NaturalOrder.cs), so anything rendered
// straight from a response is already in order. This is for the client-side table columns, where
// TanStack re-sorts on click and needs a comparator of its own.
//
// Locale is pinned rather than left to the browser: an unpinned collator would order a Polish user's
// list differently from an English one, and differently again from the server. "en" matches the
// existing numeric collator use in components/shared/Addresses/PickAddress.tsx.
const collator = new Intl.Collator("en", { numeric: true, sensitivity: "base" });

// Returns <0, 0 or >0 so it can be handed straight to Array.sort or a TanStack sortingFn.
export function naturalCompare(left?: string | null, right?: string | null): number {
  // Unnamed rows sort first rather than throwing — a blank name is a data problem to fix in the
  // admin screen, not a reason to break the table.
  const a = left ?? "";
  const b = right ?? "";

  if (!a || !b) {
    if (!a && !b) return 0;
    return !a ? -1 : 1;
  }

  const byMagnitude = collator.compare(a, b);
  if (byMagnitude !== 0) return byMagnitude;

  // sensitivity "base" keeps "a1" next to "A1" instead of exiling lower-case names below the
  // upper-case ones, but it reports them as equal. Fall back to an exact comparison so the order is
  // deterministic and the table doesn't reshuffle identical-looking rows between renders.
  if (a === b) return 0;
  return a < b ? -1 : 1;
}
